import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import {
  CANCEL_BOOKING,
  CREATE_BOOKING,
  GET_BOOKING_LIST,
  GET_USERS_URL,
  GET_BOOKING_DETAILS,
  PHARMACY_OPEN_DAYS,
  PHARMACY_SLOTS,
  SAVE_TEST_RESULTS,
  PREVIEW_TEST_RESULTS,
  RESCHEDULE_BOOKING,
  SEND_LAB_TEST_EMAIL,
  UPDATE_BOOKING_DETAILS,
} from "../../services/urls";
import { getPharmacyId } from "../../utils/commonFunctions";

// Initial state
const initialState = {
  loading: false,
  bookingsListLoading: false,
  bookingLoading: false,
  bookingDetailsLoading: false,
  bookingDetailsError: null,
  saveResultsLoading: false,
  saveResultsError: null,
  labTestBookings: { data: [], total: 0, totalPages: undefined },
  usersList: [],
  usersListLoading: false,
  error: null,
  currentBooking: null,
  pharmacySlots: null,
  pharmacySlotsLoading: false,
  pharmacyOpenDays: null,
  pharmacyOpenDaysLoading: false,
};

// Async thunk to fetch pharmacy lab tests (existing)
export const fetchLabTestBooking = createAsyncThunk(
  "labTests/fetchLabTestBooking",
  async (
    { startDate = "", endDate = "", date = "", status = "open", limit = 10, page = 1, search = "", sortField = "bookingDateTime", sortOrder = "asc" },
    { rejectWithValue }
  ) => {
    try {
      const pharmacyId = getPharmacyId();
      if (!pharmacyId) {
        return rejectWithValue("Pharmacy ID not found");
      }

      // Backward compatibility: if a single `date` is provided, map it to a single-day range
      const resolvedStartDate = date ? date : startDate;
      const resolvedEndDate = date ? date : endDate;

      const rawLimit =
        limit != null && limit !== "" ? Number(limit) : 10;
      const cappedLimit = Math.min(100, Math.max(1, Number.isFinite(rawLimit) ? rawLimit : 10));

      const params = {
        startDate: resolvedStartDate,
        endDate: resolvedEndDate,
        status,
        limit: cappedLimit,
        page,
        search,
      };

      if (sortField) {
        params.sortField = sortField;
        params.sortOrder = sortOrder || "desc";
      }

      const response = await api.get(GET_BOOKING_LIST.replace(":pharmacyId", pharmacyId), {
        params,
      });

      const root = response?.data ?? {};
      const inner = root?.data;

      /** API shape: data.bookings = { data: [...], total } OR data.bookings = [...] */
      const bookingsWrapper = inner?.bookings ?? root?.bookings;
      let list = null;
      let totalFromBookingsNode;
      let totalPagesFromBookingsNode;

      if (bookingsWrapper != null) {
        if (Array.isArray(bookingsWrapper)) {
          list = bookingsWrapper;
        } else if (typeof bookingsWrapper === "object" && Array.isArray(bookingsWrapper.data)) {
          list = bookingsWrapper.data;
          totalFromBookingsNode =
            bookingsWrapper.total ??
            bookingsWrapper.count ??
            bookingsWrapper.totalCount ??
            bookingsWrapper.totalRecords;
          totalPagesFromBookingsNode =
            bookingsWrapper.totalPages ?? bookingsWrapper.pages;
        } else {
          list = [];
        }
      }

      if (list === null) {
        list =
          (Array.isArray(inner?.bookingList) && inner.bookingList) ||
          (Array.isArray(inner?.results) && inner.results) ||
          (Array.isArray(inner?.items) && inner.items) ||
          (Array.isArray(inner?.list) && inner.list) ||
          (Array.isArray(inner?.data) && inner.data) ||
          (Array.isArray(inner) && inner) ||
          (Array.isArray(root.bookings) && root.bookings) ||
          (Array.isArray(root.results) && root.results) ||
          [];
      }

      const envForTotal =
        inner && typeof inner === "object" && !Array.isArray(inner) ? inner : root;
      let totalRaw =
        totalFromBookingsNode ??
        envForTotal.total ??
        envForTotal.count ??
        envForTotal.totalCount ??
        envForTotal.totalRecords ??
        inner?.pagination?.total ??
        root.pagination?.total;

      const totalPagesRaw =
        totalPagesFromBookingsNode ??
        inner?.pagination?.totalPages ??
        root.pagination?.totalPages ??
        envForTotal.totalPages;

      let total;
      if (totalRaw != null && totalRaw !== "") {
        const n = Number(totalRaw);
        if (Number.isFinite(n)) total = n;
      }

      let totalPages;
      if (totalPagesRaw != null && totalPagesRaw !== "") {
        const n = Number(totalPagesRaw);
        if (Number.isFinite(n) && n > 0) totalPages = n;
      }

      return {
        data: Array.isArray(list) ? list : [],
        total,
        totalPages,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pharmacy lab tests"
      );
    }
  }
);

// Async thunk to fetch pharmacy lab tests (existing)
export const fetchUsersWithNameOrEmail = createAsyncThunk(
  "labTests/fetchUsersWithNameOrEmail",
  async (search = "", { rejectWithValue }) => {
    try {
      const response = await api.post(GET_USERS_URL, {
        query: encodeURI(search),
      });
      return response.data.data; // Return the pharmacy lab test data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pharmacy lab tests"
      );
    }
  }
);

// Async thunk to fetch pharmacy slots (for date/time picker)
export const fetchPharmacySlots = createAsyncThunk(
  "labTestBooking/fetchPharmacySlots",
  async ({ date }, { rejectWithValue }) => {
    try {
      const url = PHARMACY_SLOTS.replace(":pharmacyId", getPharmacyId());
      const response = await api.post(`${url}?type=group`, { date });
      const data = response.data?.data ?? {};
      return { date, ...data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pharmacy slots"
      );
    }
  }
);

// Async thunk to fetch pharmacy open days (for date picker month)
export const fetchPharmacyOpenDays = createAsyncThunk(
  "labTestBooking/fetchPharmacyOpenDays",
  async ({ month, year }, { rejectWithValue }) => {
    try {
      const url = PHARMACY_OPEN_DAYS.replace(":pharmacyId", getPharmacyId());
      const response = await api.get(url, { params: { month, year } });
      const data = response.data?.data ?? [];
      return { month, year, dates: data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch open days"
      );
    }
  }
);

// Async thunk to cancel a users lab tests
export const cancelBooking = createAsyncThunk(
  "labTestBooking/cancelBooking",
  async ({ bookingId, userId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        CANCEL_BOOKING + `/${bookingId}/${userId}`
      );
      return response.data.data; // Assuming your API response contains the new team data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to cancel the booking"
      );
    }
  }
);

// Async thunk to cancel a users lab tests
export const updateBookingStatus = createAsyncThunk(
  "labTestBooking/updateBookingStatus",
  async (
    {
      bookingId,
      userId,
      status,
      barcode,
      startDiscountEnabled,
      startDiscountType,
      startDiscountValue,
    },
    { rejectWithValue }
  ) => {
    try {
      const body = { status, barcode };
      if (startDiscountEnabled) {
        body.startDiscountEnabled = true;
        body.startDiscountType = startDiscountType || "percentage";
        body.startDiscountValue = Number(startDiscountValue || 0);
      }
      const response = await api.put(
        CANCEL_BOOKING + `/${bookingId}/${userId}/${getPharmacyId()}`,
        body
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update the status"
      );
    }
  }
);

// Async thunk to fetch pharmacy lab tests (existing)
export const createBookingAction = createAsyncThunk(
  "labTestBooking/createBookingAction",
  async (bookingData, { rejectWithValue }) => {
    try {
      const response = await api.post(CREATE_BOOKING, bookingData);
      return response.data.data; // Return the pharmacy lab test data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pharmacy lab tests"
      );
    }
  }
);

// Async thunk to get booking details by barcode
export const getBookingDetails = createAsyncThunk(
  "labTestBooking/getBookingDetails",
  async ({ barcode, pharmacyId }, { rejectWithValue }) => {
    try {
      const resolvedPharmacyId = pharmacyId ?? getPharmacyId();
      const response = await api.get(
        GET_BOOKING_DETAILS.replace(":barcode", barcode),
        { params: { pharmacyId: resolvedPharmacyId } }
      );
      return response.data; // Return the booking details
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch booking details"
      );
    }
  }
);

// Async thunk to preview test results PDF without saving
export const previewTestResults = createAsyncThunk(
  "labTestBooking/previewTestResults",
  async ({ bookingId, barcode, results }, { rejectWithValue }) => {
    try {
      const response = await api.post(PREVIEW_TEST_RESULTS, {
        bookingId,
        barcode,
        results,
        pharmacyId: getPharmacyId(),
      });
      return response.data?.data ?? response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to preview test results"
      );
    }
  }
);

// Async thunk to save test results
export const saveTestResults = createAsyncThunk(
  "labTestBooking/saveTestResults",
  async ({ bookingId, barcode, results }, { rejectWithValue }) => {
    try {
      const response = await api.post(SAVE_TEST_RESULTS, {
        bookingId,
        barcode,
        results,
        pharmacyId: getPharmacyId(),
      });
      if (response?.data?.data?.signedUrl) {
        try {
          // const link = document.createElement('a');
          // link.href = response.data.data.signedUrl;
          // link.download = 'report.pdf';
          // document.body.appendChild(link);
          // link.click();
          // document.body.removeChild(link);
        } catch (error) {
          console.error('Error downloading PDF:', error);
        }

        // window.open(response.data.data.signedUrl, '_blank');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to save test results"
      );
    }
  }
);

// Async thunk to reschedule a booking
export const rescheduleBooking = createAsyncThunk(
  "labTestBooking/rescheduleBooking",
  async ({ bookingId, bookingDate, bookingTime }, { rejectWithValue }) => {
    try {
      const response = await api.put(RESCHEDULE_BOOKING, {
        bookingId,
        pharmacyId: getPharmacyId(),
        bookingDate,
        bookingTime,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reschedule the booking"
      );
    }
  }
);

// Async thunk to send lab test email
export const sendLabTestEmail = createAsyncThunk(
  "labTestBooking/sendLabTestEmail",
  async ({ bookingId }, { rejectWithValue }) => {
    try {
      const response = await api.post(SEND_LAB_TEST_EMAIL, { bookingId });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send lab test email"
      );
    }
  }
);

// Async thunk to update booking details
export const updateBookingDetails = createAsyncThunk(
  "labTestBooking/updateBookingDetails",
  async ({ bookingId, pharmacyId, userDetails, labTestIds }, { rejectWithValue }) => {
    try {
      const payload = {
        bookingId,
        pharmacyId,
        userDetails,
      };

      if (Array.isArray(labTestIds)) {
        payload.labTestIds = labTestIds;
      }

      const response = await api.put(UPDATE_BOOKING_DETAILS, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update booking details"
      );
    }
  }
);

const labTestBookingSlice = createSlice({
  name: "labTestBooking",
  initialState,
  reducers: {
    updateBookingInList: (state, action) => {
      const { bookingId, updatedData } = action.payload;
      const bookingsArray = Array.isArray(state.labTestBookings?.data) ? state.labTestBookings.data : [];
      const index = bookingsArray.findIndex((booking) => booking._id === bookingId);

      if (index !== -1 && state.labTestBookings?.data) {
        const currentBooking = bookingsArray[index];
        const firstName =
          updatedData.firstName !== undefined
            ? updatedData.firstName
            : currentBooking.firstName || currentBooking.fullName?.split(" ")[0] || "";
        const lastName =
          updatedData.lastName !== undefined
            ? updatedData.lastName
            : currentBooking.lastName || currentBooking.fullName?.split(" ").slice(1).join(" ") || "";
        const updatedBooking = {
          ...currentBooking,
          ...updatedData,
          firstName,
          lastName,
          fullName: `${firstName} ${lastName}`.trim(),
        };

        state.labTestBookings.data[index] = updatedBooking;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Lab Test Booking
      .addCase(fetchLabTestBooking.pending, (state) => {
        state.bookingsListLoading = true;
        state.error = null;
      })
      .addCase(fetchLabTestBooking.fulfilled, (state, action) => {
        state.bookingsListLoading = false;
        const payload = action.payload;
        if (payload && Array.isArray(payload.data)) {
          const prev = state.labTestBookings;
          const resolvedTotal =
            typeof payload.total === "number" && Number.isFinite(payload.total)
              ? payload.total
              : prev?.total ?? payload.data.length;
          const resolvedTotalPages =
            typeof payload.totalPages === "number" &&
            Number.isFinite(payload.totalPages) &&
            payload.totalPages > 0
              ? payload.totalPages
              : prev?.totalPages;
          state.labTestBookings = {
            data: payload.data,
            total: resolvedTotal,
            totalPages: resolvedTotalPages,
          };
        } else if (Array.isArray(payload)) {
          state.labTestBookings = { data: payload, total: payload.length, totalPages: undefined };
        } else {
          state.labTestBookings = { data: [], total: 0, totalPages: undefined };
        }
      })
      .addCase(fetchLabTestBooking.rejected, (state, action) => {
        state.bookingsListLoading = false;
        state.error = action.payload;
      })

      // Fetch Users With Name Or Email
      .addCase(fetchUsersWithNameOrEmail.pending, (state) => {
        state.usersListLoading = true;
      })
      .addCase(fetchUsersWithNameOrEmail.fulfilled, (state, action) => {
        state.usersListLoading = false;
        state.error = null;
        state.usersList = action.payload;
      })
      .addCase(fetchUsersWithNameOrEmail.rejected, (state, action) => {
        state.usersListLoading = false;
        state.error = action.payload;
      })

      // Fetch Pharmacy Slots
      .addCase(fetchPharmacySlots.pending, (state) => {
        state.pharmacySlotsLoading = true;
      })
      .addCase(fetchPharmacySlots.fulfilled, (state, action) => {
        state.pharmacySlotsLoading = false;
        state.pharmacySlots = action.payload;
      })
      .addCase(fetchPharmacySlots.rejected, (state) => {
        state.pharmacySlotsLoading = false;
        state.pharmacySlots = null;
      })

      // Fetch Pharmacy Open Days
      .addCase(fetchPharmacyOpenDays.pending, (state) => {
        state.pharmacyOpenDaysLoading = true;
      })
      .addCase(fetchPharmacyOpenDays.fulfilled, (state, action) => {
        state.pharmacyOpenDaysLoading = false;
        state.pharmacyOpenDays = action.payload;
      })
      .addCase(fetchPharmacyOpenDays.rejected, (state) => {
        state.pharmacyOpenDaysLoading = false;
        state.pharmacyOpenDays = null;
      })

      // Create Booking Action
      .addCase(createBookingAction.pending, (state) => {
        state.bookingLoading = true;
      })
      .addCase(createBookingAction.fulfilled, (state, action) => {
        state.bookingLoading = false;
        state.error = null;
      })
      .addCase(createBookingAction.rejected, (state, action) => {
        state.bookingLoading = false;
        state.error = action.payload;
      })

      // Get Booking Details
      .addCase(getBookingDetails.pending, (state) => {
        state.bookingDetailsLoading = true;
        state.bookingDetailsError = null;
      })
      .addCase(getBookingDetails.fulfilled, (state, action) => {
        state.bookingDetailsLoading = false;
        state.currentBooking = action.payload.data;
      })
      .addCase(getBookingDetails.rejected, (state, action) => {
        state.bookingDetailsLoading = false;
        state.bookingDetailsError = action.payload;
      })

      // Save Test Results
      .addCase(saveTestResults.pending, (state) => {
        state.saveResultsLoading = true;
        state.saveResultsError = null;
      })
      .addCase(saveTestResults.fulfilled, (state) => {
        state.saveResultsLoading = false;
      })
      .addCase(saveTestResults.rejected, (state, action) => {
        state.saveResultsLoading = false;
        state.saveResultsError = action.payload;
      })

      // Reschedule Booking
      .addCase(rescheduleBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rescheduleBooking.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(rescheduleBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Send Lab Test Email
      // Note: Not setting loading state here as component handles it locally with resendLoading
      .addCase(sendLabTestEmail.pending, (state) => {
        state.error = null;
      })
      .addCase(sendLabTestEmail.fulfilled, (state) => {
        // Email sent successfully, no need to update loading state
      })
      .addCase(sendLabTestEmail.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Update Booking Details
      .addCase(updateBookingDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBookingDetails.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateBookingDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { updateBookingInList } = labTestBookingSlice.actions;
export default labTestBookingSlice.reducer;
